import { useMenu, useSettings, useSpecials } from '@restaurant/api-client/react';
import { formatMoney, type MenuItemDto, type OrderType, type PaymentMethod } from '@restaurant/shared';
import { Minus, Plus, Search, Star, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, Input, Textarea } from '@/components/ui/input';
import { Select, Spinner, Tabs, TabsList, TabsTrigger } from '@/components/ui/misc';
import { cn, todayYmd } from '@/lib/utils';

export interface EditorLine {
  menuItemId: string;
  quantity: number;
  optionIds: string[];
  notes?: string | null;
}
export interface EditorValue {
  type: OrderType;
  items: EditorLine[];
  address: string;
  notes: string;
  paymentMethod: PaymentMethod;
}

/** Menu picker + line editor. Totals shown here are a preview; the server recomputes prices. */
export function OrderEditor({
  initial,
  submitLabel,
  onSubmit,
  secondary,
}: {
  initial: EditorValue;
  submitLabel: string;
  onSubmit: (v: EditorValue) => Promise<void>;
  secondary?: React.ReactNode;
}) {
  const { data: menu = [] } = useMenu();
  const { data: settings } = useSettings();
  const [v, setV] = useState<EditorValue>(initial);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const items = useMemo(() => new Map(menu.flatMap((c) => c.items).map((i) => [i.id, i])), [menu]);
  const currency = settings?.currency ?? 'LKR';
  // Orders are priced on today's business date, so today's special prices apply (the server does the same).
  const { data: specials = [] } = useSpecials(todayYmd(settings?.timezone));
  const special = useMemo(() => new Map(specials.map((s) => [s.menuItemId, s])), [specials]);
  const price = (i: MenuItemDto) => special.get(i.id)?.specialPriceCents ?? i.priceCents;

  const matches = useMemo(() => {
    const s = search.trim().toLowerCase();
    const all = menu.flatMap((c) => c.items);
    return s ? all.filter((i) => i.name.toLowerCase().includes(s) || i.aliases.some((a) => a.includes(s))) : all;
  }, [menu, search]);

  const add = (item: MenuItemDto) =>
    setV((p) => {
      const idx = p.items.findIndex((l) => l.menuItemId === item.id && l.optionIds.length === 0);
      if (idx >= 0) return { ...p, items: p.items.map((l, i) => (i === idx ? { ...l, quantity: l.quantity + 1 } : l)) };
      return { ...p, items: [...p.items, { menuItemId: item.id, quantity: 1, optionIds: [] }] };
    });
  const update = (idx: number, patch: Partial<EditorLine>) => setV((p) => ({ ...p, items: p.items.map((l, i) => (i === idx ? { ...l, ...patch } : l)) }));
  const remove = (idx: number) => setV((p) => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));

  const subtotal = v.items.reduce((s, l) => {
    const item = items.get(l.menuItemId);
    if (!item) return s;
    const opts = item.options.filter((o) => l.optionIds.includes(o.id)).reduce((a, o) => a + o.priceDeltaCents, 0);
    return s + (price(item) + opts) * l.quantity;
  }, 0);
  const fee = v.type === 'delivery' ? (settings?.deliveryFeeCents ?? 0) : 0;

  const submit = async () => {
    setBusy(true);
    try {
      await onSubmit(v);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-4">
      <Tabs value={v.type} onValueChange={(t) => setV((p) => ({ ...p, type: t as OrderType }))}>
        <TabsList>
          <TabsTrigger value="pickup">Pickup</TabsTrigger>
          <TabsTrigger value="delivery">Delivery</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input className="pl-8" placeholder="Add items: search the menu" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="max-h-40 overflow-y-auto rounded-md border">
          {matches.map((i) => (
            <button
              key={i.id}
              disabled={!i.available}
              onClick={() => add(i)}
              className="flex w-full items-center justify-between gap-2 border-b px-3 py-1.5 text-left text-sm last:border-0 hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="flex min-w-0 items-center gap-1.5">
                {special.has(i.id) ? <Star className="size-3.5 shrink-0 fill-amber-400 text-amber-500" aria-label="Today's special" /> : null}
                <span className="truncate">{i.name}</span>
              </span>
              <span className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                {!i.available ? <Badge variant="destructive">Sold out</Badge> : null}
                {price(i) !== i.priceCents ? <s className="opacity-60">{formatMoney(i.priceCents, currency)}</s> : null}
                {formatMoney(price(i), currency)}
                <Plus className="size-3.5" />
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-2">
        {v.items.length === 0 ? <p className="text-sm text-muted-foreground">No items yet.</p> : null}
        {v.items.map((l, idx) => {
          const item = items.get(l.menuItemId);
          return (
            <div key={idx} className="rounded-lg border p-2">
              <div className="flex items-center gap-2">
                <span className={cn('flex-1 truncate text-sm font-medium', !item && 'text-destructive')}>{item?.name ?? 'Unknown item'}</span>
                <div className="flex items-center rounded-md border">
                  <button className="p-1 hover:bg-accent" onClick={() => update(idx, { quantity: Math.max(1, l.quantity - 1) })} aria-label="Decrease"><Minus className="size-3.5" /></button>
                  <span className="w-7 text-center text-sm tabular-nums">{l.quantity}</span>
                  <button className="p-1 hover:bg-accent" onClick={() => update(idx, { quantity: Math.min(50, l.quantity + 1) })} aria-label="Increase"><Plus className="size-3.5" /></button>
                </div>
                <button className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-destructive" onClick={() => remove(idx)} aria-label="Remove"><Trash2 className="size-4" /></button>
              </div>
              {item?.options.length ? (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {item.options.map((o) => {
                    const on = l.optionIds.includes(o.id);
                    return (
                      <button
                        key={o.id}
                        onClick={() => update(idx, { optionIds: on ? l.optionIds.filter((x) => x !== o.id) : [...l.optionIds, o.id] })}
                        className={cn('rounded-full border px-2 py-0.5 text-xs', on ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent')}
                      >
                        {o.name} +{formatMoney(o.priceDeltaCents, currency)}
                      </button>
                    );
                  })}
                </div>
              ) : null}
              <Input className="mt-2 h-8 text-xs" placeholder="Item note (optional)" value={l.notes ?? ''} onChange={(e) => update(idx, { notes: e.target.value || null })} />
            </div>
          );
        })}
      </div>

      {v.type === 'delivery' ? (
        <Field label="Delivery address">
          <Textarea rows={2} value={v.address} onChange={(e) => setV((p) => ({ ...p, address: e.target.value }))} />
        </Field>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Payment">
          <Select
            value={v.paymentMethod}
            onValueChange={(p) => setV((x) => ({ ...x, paymentMethod: p as PaymentMethod }))}
            options={[{ value: 'cash', label: v.type === 'delivery' ? 'Cash on delivery' : 'Cash at pickup' }, { value: 'card_on_pickup', label: 'Card at pickup' }]}
          />
        </Field>
        <Field label="Order note">
          <Input value={v.notes} onChange={(e) => setV((p) => ({ ...p, notes: e.target.value }))} placeholder="e.g. less spicy, 7pm" />
        </Field>
      </div>

      <div className="rounded-lg bg-muted p-3 text-sm">
        <div className="flex justify-between"><span>Subtotal</span><span className="tabular-nums">{formatMoney(subtotal, currency)}</span></div>
        {fee ? <div className="flex justify-between text-muted-foreground"><span>Delivery</span><span className="tabular-nums">{formatMoney(fee, currency)}</span></div> : null}
        <div className="mt-1 flex justify-between font-semibold"><span>Total</span><span className="tabular-nums">{formatMoney(subtotal + fee, currency)}</span></div>
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        {secondary}
        <Button onClick={() => void submit()} disabled={busy || v.items.length === 0 || (v.type === 'delivery' && !v.address.trim())}>
          {busy ? <Spinner /> : null} {submitLabel}
        </Button>
      </div>
    </div>
  );
}

export function toEditorValue(o?: { type: OrderType; items: { menuItemId: string | null; quantity: number; options: { id: string }[]; notes: string | null }[]; address: string | null; notes: string | null; paymentMethod: PaymentMethod }, defaultAddress?: string | null): EditorValue {
  return {
    type: o?.type ?? 'pickup',
    items: (o?.items ?? []).filter((i) => i.menuItemId).map((i) => ({ menuItemId: i.menuItemId!, quantity: i.quantity, optionIds: i.options.map((x) => x.id), notes: i.notes })),
    address: o?.address ?? defaultAddress ?? '',
    notes: o?.notes ?? '',
    paymentMethod: o?.paymentMethod ?? 'cash',
  };
}
